import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { resolveEmployeeId } from '../../auth/utils/tokenPermissions';
import { sortContactChannels } from '../../contacts/contactChannelUtils';
import { getContacts } from '../../contacts/services/contactService';
import { Contact } from '../../contacts/types/Contact';
import { listChannelAccounts } from '../../connectors/services/channelAccountService';
import { ChannelAccount } from '../../connectors/types/channelAccount';
import { createConversation, findExistingDirectConversation as findExistingDirectConversationRequest, findExistingExternalConversation as findExistingExternalConversationRequest, getParticipantOptions, toggleAiAssistant } from '../services/conversationService';
import { isAiAssistantDirectConversation } from './useConversationPresentation';
import { Conversation, ConversationParticipantOption, ConversationParticipantRef } from '../types/Conversation';
import { NavSection } from '../types/conversationNav';
import { contactChannelTypeToAccountChannel, requiresChannelAccountForExternalCreate } from '../utils/contactChannelAccount';

type CreateConversationType = 'direct-message' | 'channel' | 'external-chat' | null;

interface UseConversationCreationOptions {
  navSection: NavSection;
  list: Conversation[];
  locationKey: string;
  prefillContactId?: string;
  accessToken?: string | null;
  employeeId?: string | null;
  tenantId?: string | null;
  isDirectConversation: (conversation: Conversation) => boolean;
  onSelectConversation: (conversationId: string) => void;
  onCreatedConversation: (conversation: Conversation) => void;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

interface UseConversationCreationResult {
  creating: boolean;
  showCreatePanel: boolean;
  createConversationType: CreateConversationType;
  createTitle: string;
  createContactSearch: string;
  participantOptions: ConversationParticipantOption[];
  selectedParticipantId: string | null;
  selectedParticipantIds: Set<string>;
  loadingParticipantOptions: boolean;
  filteredParticipantOptions: ConversationParticipantOption[];
  setCreateTitle: (value: string) => void;
  setCreateContactSearch: (value: string) => void;
  openCreatePanel: () => Promise<void>;
  onSetConversationType: (type: CreateConversationType) => Promise<void>;
  onCancelCreatePanel: () => void;
  onCreate: () => Promise<void>;
  toggleParticipantSelection: (participantId: string) => void;
  toggleChannelParticipantSelection: (participantId: string) => void;
  channelAccountsForCreate: ChannelAccount[];
  loadingChannelAccountsForCreate: boolean;
  selectedChannelAccountId: string | null;
  setSelectedChannelAccountId: (value: string | null) => void;
  requiresChannelAccountSelection: boolean;
}

export const useConversationCreation = ({
  navSection,
  list: _list,
  locationKey,
  prefillContactId,
  accessToken,
  employeeId,
  tenantId,
  isDirectConversation,
  onSelectConversation,
  onCreatedConversation,
  showToast,
}: UseConversationCreationOptions): UseConversationCreationResult => {
  const { t } = useTranslation('conversations');
  const [creating, setCreating] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [createConversationType, setCreateConversationType] = useState<CreateConversationType>(null);
  const [createTitle, setCreateTitle] = useState('');
  const [createContactSearch, setCreateContactSearch] = useState('');
  const [participantOptions, setParticipantOptions] = useState<ConversationParticipantOption[]>([]);
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<Set<string>>(new Set());
  const [loadingParticipantOptions, setLoadingParticipantOptions] = useState(false);
  const [handledPrefillKey, setHandledPrefillKey] = useState<string | null>(null);
  const [channelAccountsForCreate, setChannelAccountsForCreate] = useState<ChannelAccount[]>([]);
  const [loadingChannelAccountsForCreate, setLoadingChannelAccountsForCreate] = useState(false);
  const [selectedChannelAccountId, setSelectedChannelAccountId] = useState<string | null>(null);
  const currentEmployeeId = useMemo(() => {
    if (employeeId?.trim()) {
      return employeeId.trim();
    }

    return accessToken && tenantId ? resolveEmployeeId(accessToken, tenantId) : null;
  }, [accessToken, employeeId, tenantId]);

  const buildParticipantSelectionKey = useCallback((option: Pick<ConversationParticipantOption, 'type' | 'id'>, suffix?: string) => {
    return suffix ? `${option.type}:${option.id}:${suffix}` : `${option.type}:${option.id}`;
  }, []);

  const expandParticipantOptions = useCallback((options: ConversationParticipantOption[], contacts: Contact[]) => {
    const contactsById = new Map(contacts.map(contact => [contact.id, contact]));

    return options.flatMap(option => {
      const baseSelectionKey = buildParticipantSelectionKey(option);
      if (option.type !== 'customer') {
        return [{ ...option, selectionKey: baseSelectionKey }];
      }

      // If backend already returned channel-specific options, keep them as-is.
      if (option.contactChannelId && option.contactChannelType && option.contactChannelValue) {
        const channelKeyPart = [
          option.contactChannelId.trim(),
          option.contactChannelType,
          option.contactChannelValue.trim().toLowerCase(),
        ].join(':');

        return [{
          ...option,
          selectionKey: buildParticipantSelectionKey(option, channelKeyPart),
        }];
      }

      const contact = contactsById.get(option.id);
      const channels = contact
        ? sortContactChannels(contact.channels).filter(channel => channel.value.trim().length > 0)
        : [];

      if (channels.length === 0) {
        return [{ ...option, selectionKey: baseSelectionKey }];
      }

      return channels.map((channel, index) => {
        const channelValue = channel.value.trim();
        const channelLabel = channel.label.trim();
        const channelKeyPart = [channel.id?.trim(), channel.type, channelValue.toLowerCase(), String(index)]
          .filter(Boolean)
          .join(':');

        return {
          ...option,
          selectionKey: buildParticipantSelectionKey(option, channelKeyPart),
          contactChannelId: channel.id,
          contactChannelType: channel.type,
          contactChannelLabel: channelLabel || undefined,
          contactChannelValue: channelValue,
          isContactChannelPrimary: channel.isPrimary,
        } satisfies ConversationParticipantOption;
      });
    });
  }, [buildParticipantSelectionKey]);

  const loadParticipantOptions = useCallback(async () => {
    const [options, contacts] = await Promise.all([
      getParticipantOptions(),
      getContacts().catch(() => []),
    ]);

    return expandParticipantOptions(options, contacts);
  }, [expandParticipantOptions]);

  const resolveSelectionKey = useCallback((value: string | null | undefined, options: ConversationParticipantOption[]) => {
    if (!value) {
      return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    if (options.some(option => option.selectionKey === trimmedValue)) {
      return trimmedValue;
    }

    return options.find(option => option.id === trimmedValue)?.selectionKey ?? null;
  }, []);

  const isAllowedCreateParticipant = useCallback((option: ConversationParticipantOption, type: CreateConversationType) => {
    if (type === 'direct-message' || type === 'channel') {
      return option.type === 'companyMember';
    }

    if (type === 'external-chat') {
      return option.type === 'customer' || option.type === 'lead';
    }

    return false;
  }, []);

  useEffect(() => {
    const initializeParticipantOptions = async () => {
      try {
        const options = await loadParticipantOptions();
        setParticipantOptions(options);
      } catch {
      }
    };

    void initializeParticipantOptions();
  }, [loadParticipantOptions]);

  const filteredParticipantOptions = useMemo(() => {
    const allowedOptions = participantOptions.filter(option => {
      if (!isAllowedCreateParticipant(option, createConversationType)) return false;
      if (option.type === 'companyMember' && currentEmployeeId && option.id === currentEmployeeId) return false;
      // For external-chat, only show customer rows that have a messaging-capable channel
      if (createConversationType === 'external-chat' && option.type === 'customer') {
        return option.contactChannelType === 'whatsApp'
          || option.contactChannelType === 'instagram'
          || option.contactChannelType === 'telegram'
          || option.contactChannelType === 'messenger'
          || option.contactChannelType === 'tikTok';
      }
      return true;
    });

    const channelAwareOptions = createConversationType === 'external-chat'
      ? Array.from(allowedOptions
        .reduce((map, option) => {
          const dedupeKey = `${option.type}:${option.id}:${option.contactChannelId ?? ''}:${option.contactChannelType ?? ''}:${(option.contactChannelValue ?? '').trim().toLowerCase()}`;
          if (!map.has(dedupeKey)) {
            map.set(dedupeKey, option);
          }

          return map;
        }, new Map<string, ConversationParticipantOption>())
        .values())
      : allowedOptions;

    const query = createContactSearch.trim().toLowerCase();
    if (!query) {
      return channelAwareOptions;
    }

    return channelAwareOptions.filter(option =>
      (option.name || '').toLowerCase().includes(query)
      || (option.email || '').toLowerCase().includes(query)
      || (option.contactChannelType || '').toLowerCase().includes(query)
      || (option.contactChannelLabel || '').toLowerCase().includes(query)
      || (option.contactChannelValue || '').toLowerCase().includes(query));
  }, [createContactSearch, createConversationType, currentEmployeeId, isAllowedCreateParticipant, participantOptions]);

  const selectedExternalParticipant = useMemo(() => {
    if (createConversationType !== 'external-chat' || !selectedParticipantId) {
      return null;
    }

    return participantOptions.find(option => option.selectionKey === selectedParticipantId) ?? null;
  }, [createConversationType, participantOptions, selectedParticipantId]);

  const requiredAccountChannel = useMemo(() => {
    if (selectedExternalParticipant?.type === 'lead') {
      return null;
    }

    return contactChannelTypeToAccountChannel(selectedExternalParticipant?.contactChannelType);
  }, [selectedExternalParticipant?.contactChannelType, selectedExternalParticipant?.type]);

  const requiresChannelAccountSelection = useMemo(() => {
    if (createConversationType !== 'external-chat' || !selectedExternalParticipant) {
      return false;
    }

    if (selectedExternalParticipant.type === 'lead') {
      return true;
    }

    return requiresChannelAccountForExternalCreate(selectedExternalParticipant.contactChannelType);
  }, [createConversationType, selectedExternalParticipant]);

  useEffect(() => {
    if (!requiresChannelAccountSelection || !selectedExternalParticipant) {
      setChannelAccountsForCreate([]);
      setSelectedChannelAccountId(null);
      return;
    }

    let active = true;
    setLoadingChannelAccountsForCreate(true);

    const loadAccounts = requiredAccountChannel
      ? listChannelAccounts(requiredAccountChannel)
      : listChannelAccounts();

    void loadAccounts
      .then(response => {
        if (!active) {
          return;
        }

        const enabledAccounts = response.items.filter(account => account.isEnabled);
        setChannelAccountsForCreate(enabledAccounts);
        const defaultAccount = enabledAccounts.find(account => account.isDefault) ?? enabledAccounts[0];
        setSelectedChannelAccountId(defaultAccount?.id ?? null);
      })
      .catch(() => {
        if (!active) {
          return;
        }

        setChannelAccountsForCreate([]);
        setSelectedChannelAccountId(null);
      })
      .finally(() => {
        if (active) {
          setLoadingChannelAccountsForCreate(false);
        }
      });

    return () => {
      active = false;
    };
  }, [requiredAccountChannel, requiresChannelAccountSelection, selectedExternalParticipant, selectedParticipantId]);

  const ensureParticipantOptionsLoaded = useCallback(async () => {
    if (participantOptions.length > 0) {
      return;
    }

    setLoadingParticipantOptions(true);
    try {
      const options = await loadParticipantOptions();
      setParticipantOptions(options);
    } catch {
      showToast(t('failedToLoadParticipants'), 'error');
    } finally {
      setLoadingParticipantOptions(false);
    }
  }, [loadParticipantOptions, participantOptions.length, showToast, t]);

  const resetCreateState = useCallback(() => {
    setShowCreatePanel(false);
    setCreateConversationType(null);
    setCreateContactSearch('');
    setSelectedParticipantId(null);
    setSelectedParticipantIds(new Set());
    setCreateTitle('');
    setSelectedChannelAccountId(null);
    setChannelAccountsForCreate([]);
  }, []);

  const getDefaultCreateConversationType = useCallback((): NonNullable<CreateConversationType> => {
    if (navSection === 'channels') {
      return 'channel';
    }

    if (navSection === 'external') {
      return 'external-chat';
    }

    return 'direct-message';
  }, [navSection]);

  const openCreatePanel = useCallback(async () => {
    const prefilledType = getDefaultCreateConversationType();

    setShowCreatePanel(true);
    setCreateConversationType(prefilledType);
    setCreateContactSearch('');
    setSelectedParticipantId(null);
    setSelectedParticipantIds(new Set());
    setCreateTitle('');

    await ensureParticipantOptionsLoaded();
  }, [ensureParticipantOptionsLoaded, getDefaultCreateConversationType]);

  useEffect(() => {
    if (handledPrefillKey === locationKey) {
      return;
    }

    if (!prefillContactId?.trim()) {
      setHandledPrefillKey(locationKey);
      return;
    }

    setHandledPrefillKey(locationKey);
    setShowCreatePanel(true);
    setCreateConversationType('external-chat');
    setCreateContactSearch('');
    void ensureParticipantOptionsLoaded().then(() => {
      setSelectedParticipantId(current => current ?? prefillContactId);
    });
  }, [ensureParticipantOptionsLoaded, handledPrefillKey, locationKey, prefillContactId]);

  useEffect(() => {
    if (!prefillContactId?.trim() || participantOptions.length === 0) {
      return;
    }

    const resolvedSelectionKey = resolveSelectionKey(prefillContactId, participantOptions);
    if (!resolvedSelectionKey) {
      return;
    }

    setSelectedParticipantId(current => current === resolvedSelectionKey ? current : resolvedSelectionKey);
  }, [participantOptions, prefillContactId, resolveSelectionKey]);

  useEffect(() => {
    const allowedSelectionKeys = new Set(
      participantOptions
        .filter(option => isAllowedCreateParticipant(option, createConversationType))
        .map(option => option.selectionKey),
    );

    setSelectedParticipantId(current => current && !allowedSelectionKeys.has(current) ? null : current);
    setSelectedParticipantIds(current => {
      let changed = false;
      const next = new Set<string>();
      current.forEach(selectionKey => {
        if (allowedSelectionKeys.has(selectionKey)) {
          next.add(selectionKey);
        } else {
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [createConversationType, isAllowedCreateParticipant, participantOptions]);

  const toggleParticipantSelection = useCallback((participantId: string) => {
    setSelectedParticipantId(current => current === participantId ? null : participantId);
  }, []);

  const toParticipantKey = useCallback((participant: ConversationParticipantRef) => {
    const baseKey = `${participant.type}:${String(participant.id).trim().toLowerCase()}`;
    if (participant.type !== 'customer') {
      return baseKey;
    }

    const contactChannelId = participant.contactChannelId?.trim().toLowerCase();
    return contactChannelId ? `${baseKey}:${contactChannelId}` : baseKey;
  }, []);

  const toggleChannelParticipantSelection = useCallback((participantId: string) => {
    const targetOption = participantOptions.find(option => option.selectionKey === participantId);
    setSelectedParticipantIds(current => {
      const next = new Set(current);
      if (!targetOption) {
        if (next.has(participantId)) {
          next.delete(participantId);
        } else {
          next.add(participantId);
        }

        return next;
      }

      const participantKey = toParticipantKey({ type: targetOption.type, id: targetOption.id });
      participantOptions.forEach(option => {
        if (toParticipantKey({ type: option.type, id: option.id }) === participantKey) {
          next.delete(option.selectionKey);
        }
      });

      if (!current.has(participantId)) {
        next.add(participantId);
      }

      return next;
    });
  }, [participantOptions, toParticipantKey]);

  const onSetConversationType = useCallback(async (type: CreateConversationType) => {
    setCreateConversationType(type);
    if (type !== 'channel') {
      setCreateTitle('');
    }

    if (type !== null) {
      await ensureParticipantOptionsLoaded();
    }
  }, [ensureParticipantOptionsLoaded]);

  const onCreate = useCallback(async () => {
    if (creating) {
      return;
    }

    const participantSelectionKeysToUse = createConversationType === 'channel'
      ? Array.from(selectedParticipantIds)
      : selectedParticipantId
        ? [selectedParticipantId]
        : [];

    if (participantSelectionKeysToUse.length === 0) {
      showToast(
        createConversationType === 'channel'
          ? t('selectAtLeastOneParticipantForChannel')
          : createConversationType === 'external-chat'
            ? t('selectExternalChatRecipient')
            : t('selectAtLeastOneParticipantForDirectMessage'),
        'error',
      );
      return;
    }

    if (createConversationType === 'direct-message' && participantSelectionKeysToUse.length !== 1) {
      showToast(t('directMessagesRequireExactlyOneParticipant'), 'error');
      return;
    }

    if (createConversationType === 'channel' && participantSelectionKeysToUse.length < 2) {
      showToast(t('selectAtLeastTwoParticipantsForChannel'), 'error');
      return;
    }

    if (createConversationType === 'external-chat' && participantSelectionKeysToUse.length !== 1) {
      showToast(t('externalChatsRequireExactlyOneParticipant'), 'error');
      return;
    }

    const selectedParticipants = participantSelectionKeysToUse
      .map(selectionKey => participantOptions.find(option => option.selectionKey === selectionKey))
      .filter((participant): participant is ConversationParticipantOption => Boolean(participant));

    if (
      selectedParticipants.length !== participantSelectionKeysToUse.length
      || selectedParticipants.some(participant => !isAllowedCreateParticipant(participant, createConversationType))
    ) {
      showToast(t('someSelectedParticipantsAreNoLongerAvailable'), 'error');
      return;
    }

    if (
      createConversationType === 'external-chat'
      && selectedParticipants[0]?.type === 'customer'
      && !selectedParticipants[0]?.contactChannelId
    ) {
      showToast(t('externalChatChannelSelectionRequired'), 'error');
      return;
    }

    if (
      createConversationType === 'external-chat'
      && selectedParticipants[0]?.type === 'customer'
      && !requiresChannelAccountForExternalCreate(selectedExternalParticipant?.contactChannelType)
    ) {
      showToast(t('externalChannelAccountNotSupported'), 'error');
      return;
    }

    if (requiresChannelAccountSelection) {
      if (loadingChannelAccountsForCreate) {
        return;
      }

      if (channelAccountsForCreate.length === 0) {
        showToast(t('noChannelAccountsConfigured'), 'error');
        return;
      }

      if (!selectedChannelAccountId) {
        showToast(t('selectChannelAccount'), 'error');
        return;
      }
    }

    const participants = Array.from(new Map(
      selectedParticipants.map(participant => [
        toParticipantKey({ type: participant.type, id: participant.id, contactChannelId: participant.contactChannelId }),
        { type: participant.type, id: participant.id, contactChannelId: participant.contactChannelId } satisfies ConversationParticipantRef,
      ]),
    ).values());

    if (createConversationType === 'direct-message') {
      const existingConversation = await findExistingDirectConversationRequest(participants[0].id);
      if (existingConversation) {
        const conversation = existingConversation.isAiAssistantMuted && isAiAssistantDirectConversation(existingConversation)
          ? await toggleAiAssistant(existingConversation.id, false) ?? existingConversation
          : existingConversation;
        onCreatedConversation(conversation);
        onSelectConversation(conversation.id);
        resetCreateState();
        showToast(t('openedExistingDirectMessage'), 'info');
        return;
      }
    }

    if (createConversationType === 'external-chat') {
      const existingConversation = await findExistingExternalConversationRequest(participants[0], {
        channelAccountId: selectedChannelAccountId ?? undefined,
      });
      if (existingConversation) {
        onCreatedConversation(existingConversation);
        onSelectConversation(existingConversation.id);
        resetCreateState();
        showToast(t('openedExistingExternalChat'), 'info');
        return;
      }
    }

    setCreating(true);
    try {
      const conversationTitle = createConversationType === 'channel' ? createTitle.trim() || undefined : undefined;
      const createdConversation = await createConversation(conversationTitle, participants, {
        channelAccountId: selectedChannelAccountId ?? undefined,
      });
      const conversation = isAiAssistantDirectConversation(createdConversation)
        ? (createdConversation.isAiAssistantMuted
          ? await toggleAiAssistant(createdConversation.id, false) ?? createdConversation
          : createdConversation)
        : (createdConversation.isAiAssistantMuted
          ? createdConversation
          : await toggleAiAssistant(createdConversation.id, true) ?? createdConversation);
      onCreatedConversation(conversation);
      onSelectConversation(conversation.id);
      resetCreateState();
      showToast(t('conversationCreatedSuccessfully'), 'success');
    } catch {
      showToast(t('failedToCreateConversation'), 'error');
    } finally {
      setCreating(false);
    }
  }, [
    channelAccountsForCreate.length,
    createConversationType,
    createTitle,
    creating,
    isAllowedCreateParticipant,
    loadingChannelAccountsForCreate,
    onCreatedConversation,
    onSelectConversation,
    participantOptions,
    requiresChannelAccountSelection,
    resetCreateState,
    selectedChannelAccountId,
    selectedParticipantId,
    selectedParticipantIds,
    selectedExternalParticipant,
    showToast,
    t,
    toParticipantKey,
  ]);

  return {
    creating,
    showCreatePanel,
    createConversationType,
    createTitle,
    createContactSearch,
    participantOptions,
    selectedParticipantId,
    selectedParticipantIds,
    loadingParticipantOptions,
    filteredParticipantOptions,
    setCreateTitle,
    setCreateContactSearch,
    openCreatePanel,
    onSetConversationType,
    onCancelCreatePanel: resetCreateState,
    onCreate,
    toggleParticipantSelection,
    toggleChannelParticipantSelection,
    channelAccountsForCreate,
    loadingChannelAccountsForCreate,
    selectedChannelAccountId,
    setSelectedChannelAccountId,
    requiresChannelAccountSelection,
  };
};